import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Wallet, Plus, Pencil, Trash2, Check, RefreshCcw, Search, IndianRupee, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { StudioPayments } from "@/components/StudioPayments";

/** Finance page — invoice ledger visible only to Super Admin (Amit) + Finance / HR teams.
 *  Amit or Om can add an invoice, watch pending balances, and one-click "mark received"
 *  once payment lands. Everything is INR (₹). */

const inr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const STATUS_META = {
  pending:  { label: "Pending",   dot: "bg-red-400",   pill: "border-red-500/40 bg-red-500/10 text-red-300" },
  partial:  { label: "Partial",   dot: "bg-amber-400", pill: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
  paid:     { label: "Paid",      dot: "bg-emerald-400", pill: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
};

export default function Finance() {
  const { user } = useAuth();
  const canView = !!user && (user.is_super_admin || user.team === "Finance Team" || user.team === "HR + Admin");
  const [tab, setTab] = useState("invoices");
  if (!canView) return <InvoiceLedger />;
  return (
    <div>
      <div className="px-4 sm:px-6 pt-4 sm:pt-6 flex gap-1" role="tablist">
        {[["invoices", "Invoice ledger"], ["studio", "LIME Studios payments"]].map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`finance-tab-${k}`}
            className={`h-8 px-3 rounded text-sm transition-colors ${tab === k ? "bg-[#E11D2E]/10 text-[#E11D2E] font-semibold" : "text-white/60 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      {tab === "invoices" ? <InvoiceLedger /> : <div className="p-4 sm:p-6"><StudioPayments /></div>}
    </div>
  );
}

function InvoiceLedger() {
  const { user } = useAuth();
  const canView = !!user && (user.is_super_admin || user.team === "Finance Team" || user.team === "HR + Admin");

  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({ total_amount: 0, total_received: 0, total_pending: 0, count: 0, by_status: {} });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    client: "", invoice_ref: "", invoice_date: "",
    amount: "", amount_received: "", notes: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [invRes, sumRes] = await Promise.all([
        api.get("/invoices"),
        api.get("/invoices/summary"),
      ]);
      setInvoices(invRes.data || []);
      setSummary(sumRes.data || {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canView) return;
    api.get("/projects/clients").then(({ data }) => setClients(data.clients || [])).catch(() => setClients([]));
    load();
  }, [canView]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      client: "", invoice_ref: "", invoice_date: new Date().toISOString().slice(0, 10),
      amount: "", amount_received: "", notes: "",
    });
    setDialogOpen(true);
  };

  const openEdit = (inv) => {
    setEditing(inv);
    setForm({
      client: inv.client || "",
      invoice_ref: inv.invoice_ref || "",
      invoice_date: (inv.invoice_date || "").slice(0, 10),
      amount: String(inv.amount ?? ""),
      amount_received: String(inv.amount_received ?? ""),
      notes: inv.notes || "",
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client.trim()) return toast.error("Client is required");
    if (!form.invoice_ref.trim()) return toast.error("Invoice # / reference is required");
    const amt = parseFloat(form.amount);
    if (!Number.isFinite(amt) || amt <= 0) return toast.error("Amount must be greater than 0");
    const rec = form.amount_received === "" ? 0 : parseFloat(form.amount_received);
    if (!Number.isFinite(rec) || rec < 0) return toast.error("Amount received must be zero or more");
    setSaving(true);
    const payload = {
      client: form.client.trim(),
      invoice_ref: form.invoice_ref.trim(),
      invoice_date: form.invoice_date || null,
      amount: amt,
      amount_received: rec,
      notes: form.notes.trim(),
    };
    try {
      if (editing) {
        await api.patch(`/invoices/${editing.id}`, payload);
        toast.success("Invoice updated");
      } else {
        await api.post("/invoices", payload);
        toast.success("Invoice added");
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save invoice");
    } finally {
      setSaving(false);
    }
  };

  const markReceived = async (inv) => {
    if (!window.confirm(`Mark ${inv.invoice_ref} (${inr(inv.amount)}) as fully received?`)) return;
    try {
      await api.post(`/invoices/${inv.id}/mark-received`);
      toast.success("Marked as received");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to update");
    }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_ref}? This cannot be undone.`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Invoice deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to delete");
    }
  };

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return invoices.filter((inv) => {
      if (statusFilter !== "ALL") {
        // "Outstanding" bundles Pending + Partial — both have balance > 0.
        if (statusFilter === "outstanding") {
          if (inv.status !== "pending" && inv.status !== "partial") return false;
        } else if (inv.status !== statusFilter) return false;
      }
      if (!needle) return true;
      const hay = `${inv.client} ${inv.invoice_ref} ${inv.notes || ""}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [invoices, q, statusFilter]);

  // Split the top-level Pending total into "unpaid" (invoices with 0 received)
  // and "partial remainder" (balance of partially-paid invoices), so users can
  // see at a glance how much of the outstanding money is stuck on partials.
  const pendingBreakdown = useMemo(() => {
    let unpaid = 0;
    let partialRemainder = 0;
    invoices.forEach((inv) => {
      if (inv.status === "pending") unpaid += inv.balance || 0;
      else if (inv.status === "partial") partialRemainder += inv.balance || 0;
    });
    return { unpaid: Math.round(unpaid * 100) / 100, partialRemainder: Math.round(partialRemainder * 100) / 100 };
  }, [invoices]);

  if (!canView) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-white/70">
        <div className="rounded-lg border border-white/10 bg-black/40 p-6">
          <h1 className="text-xl font-display mb-2">Finance</h1>
          <p className="text-sm text-white/50">
            You don&apos;t have access to the Finance ledger. Ask a Super Admin or a Finance-team member to open it for you.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="finance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[0.7rem] uppercase tracking-[0.3em] text-white/40 flex items-center gap-2">
            <Wallet size={12} /> Finance
          </div>
          <h1 className="text-3xl sm:text-4xl font-display tracking-tight">Invoice ledger</h1>
          <p className="text-sm text-white/50 mt-1">Track every invoice raised, payments received, and outstanding balances.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={load}
            data-testid="finance-refresh-btn"
            className="border-white/15 hover:border-white/30"
          >
            <RefreshCcw size={14} className="mr-1.5" /> Refresh
          </Button>
          <Button
            type="button"
            onClick={openCreate}
            data-testid="finance-add-btn"
            className="bg-[#E11D2E] hover:bg-[#F03041] rounded-full px-5"
          >
            <Plus size={14} className="mr-1.5" /> New Invoice
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryCard
          testid="finance-summary-total"
          label="Total Invoiced"
          value={inr(summary.total_amount)}
          sub={`${summary.count || 0} invoice${(summary.count || 0) === 1 ? "" : "s"}`}
          accent="border-white/10 bg-white/[0.03]"
        />
        <SummaryCard
          testid="finance-summary-received"
          label="Received"
          value={inr(summary.total_received)}
          sub={`${summary.by_status?.paid || 0} paid`}
          accent="border-emerald-500/30 bg-emerald-500/5 text-emerald-100"
        />
        <SummaryCard
          testid="finance-summary-pending"
          label="Pending"
          value={inr(summary.total_pending)}
          sub={
            pendingBreakdown.partialRemainder > 0
              ? `${inr(pendingBreakdown.unpaid)} unpaid · ${inr(pendingBreakdown.partialRemainder)} partial-remaining`
              : `${(summary.by_status?.pending || 0) + (summary.by_status?.partial || 0)} outstanding`
          }
          accent="border-red-500/30 bg-red-500/5 text-red-100"
          onClick={() => setStatusFilter("outstanding")}
          title="Click to filter to outstanding invoices"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-stretch gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            data-testid="finance-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by client, invoice #, or notes…"
            className="pl-9 bg-black/40 border-white/10 focus-visible:ring-red-500"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="finance-status-filter" className="bg-black/40 border-white/10 sm:w-52">
            <div className="flex items-center gap-2"><Filter size={12} /><SelectValue /></div>
          </SelectTrigger>
          <SelectContent className="bg-surface border-white/10 text-white">
            <SelectItem value="ALL">All statuses</SelectItem>
            <SelectItem value="outstanding">Outstanding (Pending + Partial)</SelectItem>
            <SelectItem value="pending">Pending only (0 received)</SelectItem>
            <SelectItem value="partial">Partially paid</SelectItem>
            <SelectItem value="paid">Fully paid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-white/10 bg-black/30 overflow-hidden">
        <div className="hidden md:grid grid-cols-[1.4fr_1.2fr_0.9fr_0.9fr_0.9fr_0.9fr_0.7fr_1fr] gap-3 px-4 py-2.5 text-[0.7rem] uppercase tracking-widest text-white/50 border-b border-white/5 bg-black/40">
          <div>Client · Invoice #</div>
          <div>Notes</div>
          <div>Date</div>
          <div className="text-right">Amount</div>
          <div className="text-right">Received</div>
          <div className="text-right">Balance</div>
          <div>Status</div>
          <div className="text-right">Actions</div>
        </div>
        {loading ? (
          <div className="p-6 text-white/50 text-sm">Loading invoices…</div>
        ) : visible.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-white/50 text-sm">No invoices yet.</div>
            <Button
              type="button"
              onClick={openCreate}
              className="mt-3 bg-[#E11D2E] hover:bg-[#F03041] rounded-full px-5"
              data-testid="finance-empty-add-btn"
            >
              <Plus size={14} className="mr-1.5" /> Add your first invoice
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {visible.map((inv) => {
              const meta = STATUS_META[inv.status] || STATUS_META.pending;
              return (
                <div
                  key={inv.id}
                  data-testid={`finance-row-${inv.id}`}
                  className="grid grid-cols-1 md:grid-cols-[1.4fr_1.2fr_0.9fr_0.9fr_0.9fr_0.9fr_0.7fr_1fr] gap-3 items-center px-4 py-3 hover:bg-white/[0.02] transition"
                >
                  <div>
                    <div className="text-sm font-medium truncate">{inv.client}</div>
                    <div className="text-xs text-white/50 font-mono truncate">{inv.invoice_ref}</div>
                  </div>
                  <div className="text-xs text-white/60 truncate max-w-full" title={inv.notes || ""}>
                    {inv.notes || <span className="text-white/25">—</span>}
                  </div>
                  <div className="text-xs text-white/60 font-mono">{inv.invoice_date || "—"}</div>
                  <div className="text-sm font-mono md:text-right">{inr(inv.amount)}</div>
                  <div className="text-sm font-mono md:text-right text-emerald-300/90">{inr(inv.amount_received)}</div>
                  <div className={`text-sm font-mono md:text-right ${inv.balance > 0 ? "text-red-300" : "text-white/40"}`}>
                    {inr(inv.balance)}
                  </div>
                  <div>
                    <span className={`inline-flex items-center gap-1.5 text-[0.7rem] uppercase tracking-widest px-2 py-0.5 rounded-full border ${meta.pill}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} /> {meta.label}
                    </span>
                    {inv.status === "partial" && inv.balance > 0 && (
                      <div
                        className="mt-1 text-[0.7rem] font-mono text-red-300/90"
                        data-testid={`finance-partial-remaining-${inv.id}`}
                        title="Still pending on this invoice"
                      >
                        {inr(inv.balance)} still pending
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-1.5">
                    {inv.status !== "paid" && (
                      <button
                        type="button"
                        onClick={() => markReceived(inv)}
                        title="Mark as received"
                        data-testid={`finance-received-btn-${inv.id}`}
                        className="h-7 w-7 rounded-full flex items-center justify-center bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-300 transition"
                      >
                        <Check size={13} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => openEdit(inv)}
                      title="Edit"
                      data-testid={`finance-edit-btn-${inv.id}`}
                      className="h-7 w-7 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/15 text-white/70 transition"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(inv)}
                      title="Delete"
                      data-testid={`finance-delete-btn-${inv.id}`}
                      className="h-7 w-7 rounded-full flex items-center justify-center bg-red-500/15 hover:bg-red-500/25 border border-red-500/40 text-red-300 transition"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <IndianRupee size={16} /> {editing ? "Edit Invoice" : "New Invoice"}
            </DialogTitle>
            <DialogDescription className="text-white/50">
              {editing ? "Update amount, mark payments received, or tweak notes." : "Log a new invoice raised for a client."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="overline">Client <span className="text-[#E11D2E]">*</span></Label>
                <Input
                  list="finance-client-suggestions"
                  data-testid="finance-form-client"
                  value={form.client}
                  onChange={(e) => setForm({ ...form, client: e.target.value })}
                  className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500"
                  placeholder="Client name"
                  required
                />
                <datalist id="finance-client-suggestions">
                  {clients.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div>
                <Label className="overline">Invoice # / Ref <span className="text-[#E11D2E]">*</span></Label>
                <Input
                  data-testid="finance-form-ref"
                  value={form.invoice_ref}
                  onChange={(e) => setForm({ ...form, invoice_ref: e.target.value })}
                  className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500"
                  placeholder="LIM-2026-001"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="overline">Date</Label>
                <Input
                  type="date"
                  data-testid="finance-form-date"
                  value={form.invoice_date}
                  onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                  className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500 [color-scheme:dark]"
                />
              </div>
              <div>
                <Label className="overline">Amount (₹) <span className="text-[#E11D2E]">*</span></Label>
                <Input
                  type="number"
                  min="1"
                  step="0.01"
                  data-testid="finance-form-amount"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500"
                  placeholder="50000"
                  required
                />
              </div>
              <div>
                <Label className="overline">Received (₹)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  data-testid="finance-form-received"
                  value={form.amount_received}
                  onChange={(e) => setForm({ ...form, amount_received: e.target.value })}
                  className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500"
                  placeholder="0"
                />
              </div>
            </div>

            <div>
              <Label className="overline">Notes</Label>
              <Textarea
                data-testid="finance-form-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="bg-black/40 border-white/10 mt-2 min-h-[70px] focus-visible:ring-red-500"
                placeholder="What is this invoice for? Any payment terms?"
              />
            </div>

            <DialogFooter>
              <Button
                type="submit"
                disabled={saving}
                data-testid="finance-form-submit"
                className="bg-[#E11D2E] hover:bg-[#F03041] rounded-full px-6"
              >
                {saving ? "Saving…" : editing ? "Update Invoice" : "Add Invoice"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, sub, accent, testid, onClick, title }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`w-full text-left rounded-lg border p-4 ${accent} ${onClick ? "hover:brightness-125 cursor-pointer transition" : ""}`}
    >
      <div className="text-[0.7rem] uppercase tracking-widest text-white/50">{label}</div>
      <div className="text-2xl font-display mt-1 truncate">{value}</div>
      <div className="text-xs text-white/50 mt-1">{sub}</div>
    </Tag>
  );
}
